/**
 * Modal Unlock Confirmation Handler
 *
 * Utility function that orchestrates the unlock confirmation flow for a selected user.
 * Separates unlock business logic from React hooks and state management, making the
 * handler easier to test and reuse.
 */

import { ManagerCertificationUnlockResult, unlockCertificationForManagedUser } from '@/services';
import { IManagedUserRecentSummary, IManagerProduct, IUser } from '@/types';

/**
 * Configuration object for the unlock confirmation handler.
 *
 * @property currentUser - The logged-in manager performing the unlock
 * @property managedUsers - List of all managed users (used to find target user details)
 * @property managerActiveProduct - Currently selected certification product
 * @property selectedUserId - The user ID being unlocked
 * @property selectedScenarioId - The scenario ID being unlocked
 */
type HandleUnlockConfirmConfig = {
  currentUser: IUser | null;
  managedUsers: IManagedUserRecentSummary[];
  managerActiveProduct: IManagerProduct | null;
  selectedUserId: string;
  selectedScenarioId: number | null;
};

/**
 * Evaluates and executes a certification unlock request.
 *
 * Process:
 * 1. Derive lock state from target user's recent certificate (passed status + attempt count)
 * 2. Build unlock input with all context (manager, user, scenario, lock state)
 * 3. Call the unlock service (which runs policy validation + API call)
 * 4. Return result to caller for UI state updates
 *
 * The function performs no side effects on React state; callers are responsible for
 * interpreting the result and updating UI accordingly (close modal, show error, etc.).
 *
 * @param config - Unlock context and selected user/scenario
 * @returns Result object indicating success or denial reason
 *
 * @throws Only if an unexpected error occurs; all policy denials are returned as results
 *
 * @example
 * const result = await handleModalUnlockConfirm({
 *   currentUser: { uuid: 'mgr-123', ... },
 *   managedUsers: [...],
 *   managerActiveProduct: { certificationId: 100, ... },
 *   selectedUserId: 'user-456',
 *   selectedScenarioId: 100,
 * });
 *
 * if (result.success) {
 *   // Close modal and clear UI state
 * } else {
 *   // Display denial message based on result.reason
 * }
 */
export const handleModalUnlockConfirm = async (
  config: HandleUnlockConfirmConfig,
): Promise<ManagerCertificationUnlockResult> => {
  // ──────────────────────────────────────────────────────────────────────────────
  // Step 1: Resolve target user details
  // ──────────────────────────────────────────────────────────────────────────────
  // Find the summary for the user being unlocked so we can extract their certificate
  // status and attempt count. This data is used to determine if they're locked.
  const targetUserSummary = config.managedUsers.find(
    (user) => user.userId === config.selectedUserId,
  );

  // ──────────────────────────────────────────────────────────────────────────────
  // Step 2: Derive lock state
  // ──────────────────────────────────────────────────────────────────────────────
  // The lock is engaged when:
  // - The user has NOT passed (passed !== true)
  // - AND they've made 2+ attempts (attemptCount >= 2)
  //
  // This logic mirrors the lock-derivation in the UI (dashboard table, modal trigger).
  const mostRecentCert = targetUserSummary?.mostRecentCertificate;
  const targetAttemptCount = mostRecentCert?.attemptCount ?? 0;
  const targetUserLocked = mostRecentCert?.passed !== true && targetAttemptCount >= 2;

  // ──────────────────────────────────────────────────────────────────────────────
  // Step 3: Call unlock service
  // ──────────────────────────────────────────────────────────────────────────────
  // Build the input context and delegate to the application service. The service
  // will run policy evaluation followed by the API call if allowed.
  const result = await unlockCertificationForManagedUser({
    activeCertificationId: config.managerActiveProduct?.certificationId ?? null,
    directReportUserIds: config.managedUsers.map((user) => user.userId),
    managerUserId: config.currentUser?.uuid ?? '',
    scenarioId: config.selectedScenarioId || null,
    targetUserId: config.selectedUserId,
    targetUserMostRecentCertPassed: mostRecentCert?.passed ?? null,
    targetUserScenarioLocked: targetUserLocked,
  });

  // Return the result so the caller can handle UI updates (close modal, show error, etc.)
  return result;
};
